import { useState, useCallback } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import type { SceneStep } from '../types';

interface StepSliderProps {
  steps: [SceneStep, SceneStep, SceneStep];
}

const TAB_LABELS = ['Before', '输入', 'After'] as const;
const STEP_BG_COLORS = ['#fafafa', '#f5f5f7', '#f0f4f0'] as const;
const STEP_BORDER_COLORS = [
  'rgba(0, 0, 0, 0.04)',
  'rgba(0, 0, 0, 0.05)',
  'rgba(0, 0, 0, 0.06)',
] as const;

const easeOut = [0.22, 1, 0.36, 1] as const;

export function StepSlider({ steps }: StepSliderProps): JSX.Element {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const goToStep = useCallback(
    (index: number) => {
      if (index < 0 || index > 2) return;
      setDirection(index > activeIndex ? 1 : -1);
      setActiveIndex(index);
    },
    [activeIndex]
  );

  const handleDragEnd = useCallback(
    (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      const threshold = 50;
      if (info.offset.x < -threshold && activeIndex < 2) {
        goToStep(activeIndex + 1);
      } else if (info.offset.x > threshold && activeIndex > 0) {
        goToStep(activeIndex - 1);
      }
    },
    [activeIndex, goToStep]
  );

  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 260 : -260,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -260 : 260,
      opacity: 0,
    }),
  };

  const currentStep = steps[activeIndex];
  const currentColor = STEP_BG_COLORS[activeIndex];
  const currentBorder = STEP_BORDER_COLORS[activeIndex];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Tab indicators */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '28px',
          padding: '14px 16px 8px',
          flexShrink: 0,
        }}
      >
        {TAB_LABELS.map((label, i) => (
          <button
            key={label}
            onClick={() => goToStep(i)}
            style={{
              background: 'none',
              border: 'none',
              padding: '6px 4px',
              fontSize: '12px',
              fontWeight: i === activeIndex ? 600 : 400,
              color: i === activeIndex ? '#1a1a1c' : '#a1a1a6',
              cursor: 'pointer',
              position: 'relative',
              transition: 'color 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
              letterSpacing: '0.06em',
            }}
          >
            {label}
            {i === activeIndex && (
              <motion.div
                layoutId="activeTabUnderline"
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: '-4px',
                  right: '-4px',
                  height: '1.5px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '1px',
                }}
                transition={{ type: 'spring', stiffness: 500, damping: 35 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Step counter dots */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '5px',
          flexShrink: 0,
          paddingBottom: '8px',
        }}
      >
        {TAB_LABELS.map((_, i) => (
          <div
            key={i}
            style={{
              width: i === activeIndex ? '14px' : '4px',
              height: '3px',
              borderRadius: '1.5px',
              backgroundColor: i === activeIndex ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.06)',
              transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          />
        ))}
      </div>

      {/* Swipeable content area */}
      <div
        style={{
          position: 'relative',
          overflow: 'hidden',
          flex: 1,
          minHeight: 0,
          padding: '0 12px 12px',
        }}
      >
        <AnimatePresence custom={direction} mode="wait">
          <motion.div
            key={activeIndex}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'tween', duration: 0.3, ease: easeOut }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.15}
            onDragEnd={handleDragEnd}
            style={{
              backgroundColor: currentColor,
              borderRadius: '16px',
              padding: '24px 20px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '14px',
              cursor: 'grab',
              border: `1px solid ${currentBorder}`,
              height: '100%',
              boxSizing: 'border-box',
              justifyContent: 'center',
            }}
          >
            {/* Step badge */}
            <div
              style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#a1a1a6',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
              }}
            >
              步骤 {String(activeIndex + 1).padStart(2, '0')} / 03
            </div>

            {/* Title */}
            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.3, ease: easeOut }}
              style={{
                margin: 0,
                fontSize: '17px',
                fontWeight: 600,
                color: '#1a1a1c',
                lineHeight: 1.3,
                letterSpacing: '0.01em',
              }}
            >
              {currentStep.title}
            </motion.h3>

            {/* Divider */}
            <div
              style={{
                width: '20px',
                height: '1px',
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              }}
            />

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.3, ease: easeOut }}
              style={{
                margin: 0,
                fontSize: '14px',
                color: '#6e6e73',
                lineHeight: 1.7,
                fontWeight: 400,
                maxWidth: '300px',
              }}
            >
              {currentStep.description}
            </motion.p>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Swipe hint */}
      <p
        style={{
          textAlign: 'center',
          fontSize: '11px',
          color: '#c7c7cc',
          margin: 0,
          padding: '0 16px 10px',
          letterSpacing: '0.06em',
          flexShrink: 0,
        }}
      >
        ← 左右滑动切换步骤 →
      </p>
    </div>
  );
}
